import {
  Box,
  Button,
  Chip,
  type ChipProps,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";
import { useOrbitLOnboardingActionRequiredCountQuery } from "Api/Queries/Merchants/useOrbitLOnboardingActionRequiredCountQuery";
import { useOrbitLOnboardingListQuery } from "Api/Queries/Merchants/useOrbitLOnboardingListQuery";
import {
  ComgateOnboardingStatus,
  type OrbitLOnboardingListItemDto,
  OrbitLPhase,
  TerminalFinancingModel,
} from "Infrastructure/Api/Api";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { FindByTerminalDialog } from "Components/Merchants/OrbitLOnboarding/FindByTerminalDialog";
import { OrbitLOnboardingDetailModal } from "Components/Merchants/OrbitLOnboarding/OrbitLOnboardingDetailModal";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import {
  formatTimeSinceSubmit,
  isComgateStatusRequiringAction,
} from "Components/Merchants/OrbitLOnboarding/utils";
import { useServerTable } from "Hooks/useServerTable";
import { useSearchParams } from "Navigation/useSearchParams";
import {
  asBooleanFromString,
  asStartOfDayISO,
  asString,
  asStringArray,
} from "Utils/FilterUtils";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding;

// Query param the terminal-count-change notification deep-links with; must match the link the
// backend builds in RequestTerminalCountChangeCommand.
const COMPANY_NUMBER_PARAM = "orbitLCompanyNumber";

type ChipColor = ChipProps["color"];

const PHASES_TO_COLORS: Record<OrbitLPhase, ChipColor> = {
  RequiresCompletion: "warning",
  ReadyToDownloadContract: "info",
  ContractDownloaded: "info",
  SubmittedToComgate: "primary",
  ProcessingByComgate: "primary",
  TerminalDispatched: "success",
  Completed: "success",
};

// Keep in sync with OrbitLOnboardingListFilterRequest.AllowedSortColumns on BE.
// Values are paths into the withPhase projection on the server.
const SORT_COLUMN_MAP: Record<string, string> = {
  companyNumber: "CompanyNumber",
  companyName: "CompanyName",
  hunterName: "HunterName",
  financingModel: "Detail.FinancingModel",
  orbitLActivatedAt: "OrbitLActivatedAt",
  orbitLPhase: "Phase",
  comgateStatus: "Detail.ComgateStatus",
  comgateMID: "Detail.ComgateMID",
  submittedToComgateAt: "Detail.SubmittedToComgateAt",
  lastStatusChangeAt: "LastStatusChangeAt",
};

const getColorByStatus = (
  status: ComgateOnboardingStatus | null | undefined,
): ChipColor => {
  if (!status) {
    return "default";
  }
  if (isComgateStatusRequiringAction(status)) {
    return "error";
  }
  if (status === ComgateOnboardingStatus.READY) {
    return "success";
  }
  if (
    status === ComgateOnboardingStatus.REQUESTED ||
    status === ComgateOnboardingStatus.PROCESSING
  ) {
    return "primary";
  }
  return "default";
};

const createColumns = ({
  t,
}: {
  t: TFunction;
}): Array<MRT_ColumnDef<OrbitLOnboardingListItemDto>> => [
  {
    accessorKey: "companyNumber",
    header: t(RESOURCES.Table.CompanyNumber),
    size: 110,
  },
  {
    accessorKey: "companyName",
    header: t(RESOURCES.Table.CompanyName),
  },
  {
    accessorKey: "hunterName",
    header: t(RESOURCES.Table.Hunter),
    enableSorting: false,
    size: 150,
  },
  {
    accessorKey: "financingModel",
    header: t(RESOURCES.Table.FinancingModel),
    filterVariant: "multi-select",
    filterSelectOptions: Object.values(TerminalFinancingModel).map((value) => ({
      label: t(Resources.Components.Merchant.OrbitL.FinancingModel[value]),
      value,
    })),
    Cell: ({ row }) => {
      const model = row.original.financingModel;
      if (!model) {
        return null;
      }
      return t(Resources.Components.Merchant.OrbitL.FinancingModel[model]);
    },
    size: 110,
  },
  {
    accessorKey: "orbitLActivatedAt",
    header: t(RESOURCES.Table.ActivatedAt),
    filterVariant: "date",
    Cell: ({ row }) =>
      row.original.orbitLActivatedAt
        ? formatDate(row.original.orbitLActivatedAt)
        : "",
    size: 175,
  },
  {
    accessorKey: "orbitLPhase",
    header: t(RESOURCES.Table.Phase),
    filterVariant: "multi-select",
    filterSelectOptions: Object.values(OrbitLPhase).map((value) => ({
      label: t(Resources.Components.Merchant.OrbitL.Phase[value]),
      value,
    })),
    Cell: ({ row }) => {
      const phase = row.original.orbitLPhase;
      const label = t(Resources.Components.Merchant.OrbitL.Phase[phase]);
      const color = PHASES_TO_COLORS[phase] ?? "default";

      return <Chip label={label} color={color} size="small" />;
    },
    size: 180,
  },
  {
    accessorKey: "comgateStatus",
    header: t(RESOURCES.Table.ComgateStatus),
    filterVariant: "multi-select",
    filterSelectOptions: Object.values(ComgateOnboardingStatus).map(
      (value) => ({
        label: value,
        value,
      }),
    ),
    Cell: ({ row }) => {
      const status = row.original.comgateStatus;
      if (!status) {
        return null;
      }
      return (
        <Chip
          label={status}
          color={getColorByStatus(status)}
          size="small"
          variant="outlined"
        />
      );
    },
    size: 180,
  },
  {
    accessorKey: "comgateMID",
    header: t(RESOURCES.Table.ComgateMID),
    size: 130,
  },
  {
    accessorKey: "contractedTerminalCount",
    header: t(RESOURCES.Table.TerminalCount),
    enableColumnFilter: false,
    enableSorting: false,
    size: 100,
  },
  {
    accessorKey: "requestedTerminalCount",
    header: t(RESOURCES.Table.TerminalCountRequest),
    filterVariant: "checkbox",
    muiFilterCheckboxProps: { title: t(RESOURCES.Table.OnlyOpenRequests) },
    enableSorting: false,
    size: 170,
    Cell: ({ row }) => {
      const requested = row.original.requestedTerminalCount;
      if (requested === null || requested === undefined) {
        return null;
      }
      return (
        <Chip
          label={`${row.original.contractedTerminalCount} → ${requested}`}
          color="warning"
          size="small"
        />
      );
    },
  },
  {
    accessorKey: "lastStatusChangeAt",
    header: t(RESOURCES.Table.LastStatusChange),
    enableColumnFilter: false,
    Cell: ({ row }) => formatDate(row.original.lastStatusChangeAt, true),
    size: 160,
  },
  {
    accessorKey: "submittedToComgateAt",
    header: t(RESOURCES.Table.TimeSinceSubmit),
    enableColumnFilter: false,
    Cell: ({ row }) =>
      formatTimeSinceSubmit(row.original.submittedToComgateAt, t),
    size: 130,
  },
];

type Props = {
  isEditingEnabled?: boolean;
};

export const OrbitLOnboardingTable = ({ isEditingEnabled = false }: Props) => {
  const [selectedMerchantPublicID, setSelectedMerchantPublicID] = useState<
    string | undefined
  >();
  const [isFindByTerminalOpen, setIsFindByTerminalOpen] = useState(false);

  // Deep link from the bell notification about a terminal-count request lands here filtered on
  // the merchant that asked.
  const [searchParams] = useSearchParams();
  const linkedCompanyNumber = searchParams.get(COMPANY_NUMBER_PARAM);

  const { queryParams, tableProps } =
    useServerTable<OrbitLOnboardingListItemDto>({
      defaultSorting: [],
      initialColumnFilters: linkedCompanyNumber
        ? [{ id: "companyNumber", value: linkedCompanyNumber }]
        : [],
    });

  const activatedFrom = asStartOfDayISO(queryParams.filters.orbitLActivatedAt);

  const { data, isLoading, isError, isFetching } = useOrbitLOnboardingListQuery(
    {
      filter: {
        offset: queryParams.offset,
        limit: queryParams.limit,
        sortBy: queryParams.sortBy
          ? SORT_COLUMN_MAP[queryParams.sortBy]
          : undefined,
        sortDirection: queryParams.sortDirection,
        companyName: asString(queryParams.filters.companyName),
        companyNumber: asString(queryParams.filters.companyNumber),
        hunterName: asString(queryParams.filters.hunterName),
        comgateMID: asString(queryParams.filters.comgateMID),
        comgateStatuses: asStringArray(queryParams.filters.comgateStatus),
        financingModels: asStringArray(queryParams.filters.financingModel),
        orbitLPhases: asStringArray(queryParams.filters.orbitLPhase),
        hasOpenTerminalCountRequest: asBooleanFromString(
          queryParams.filters.requestedTerminalCount,
        ),
        activatedFrom,
      },
    },
  );
  const {
    data: actionRequiredData,
    isLoading: isActionRequiredLoading,
    isError: isActionRequiredError,
  } = useOrbitLOnboardingActionRequiredCountQuery();
  const localization = useTableLocalization();
  const { t } = useResource();

  const columns = useMemo(() => createColumns({ t }), [t]);

  const actionRequiredCount = actionRequiredData?.actionRequiredCount ?? 0;
  const totalCount = data?.totalCount ?? 0;

  const table = useMaterialReactTable<OrbitLOnboardingListItemDto>({
    ...tableProps,
    data: data?.items ?? [],
    columns,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    rowCount: totalCount,
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isFetching,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setSelectedMerchantPublicID(row.original.merchantPublicID),
      sx: { cursor: "pointer" },
    }),
    renderTopToolbarCustomActions: () => (
      <Button
        variant="outlined"
        size="small"
        startIcon={<SearchIcon />}
        onClick={() => setIsFindByTerminalOpen(true)}
      >
        {t(RESOURCES.FindByTerminal.Button)}
      </Button>
    ),
  });

  if (isError) {
    return <ErrorText>{t(Resources.Errors.DataLoadingError)}</ErrorText>;
  }

  const hasNoData =
    !isLoading &&
    totalCount === 0 &&
    Object.keys(queryParams.filters).length === 0;

  if (hasNoData) {
    return null;
  }

  return (
    <>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h4" component="h2" fontSize="1.125rem">
            {t(RESOURCES.Title)}
          </Typography>
          {isActionRequiredLoading && (
            <CircularProgress size={16} color="error" />
          )}
          {!isActionRequiredLoading &&
            !isActionRequiredError &&
            actionRequiredCount > 0 && (
              <Chip label={actionRequiredCount} color="error" size="small" />
            )}
        </Stack>
        <Box display="grid" alignItems="start">
          <MaterialReactTable table={table} />
        </Box>
      </Stack>

      {isFindByTerminalOpen && (
        <FindByTerminalDialog
          onTerminalSelect={(merchantPublicID) => {
            setIsFindByTerminalOpen(false);
            setSelectedMerchantPublicID(merchantPublicID);
          }}
          onClose={() => setIsFindByTerminalOpen(false)}
        />
      )}

      {selectedMerchantPublicID && (
        <OrbitLOnboardingDetailModal
          isOpen
          isEditingEnabled={isEditingEnabled}
          merchantPublicID={selectedMerchantPublicID}
          onClose={() => setSelectedMerchantPublicID(undefined)}
        />
      )}
    </>
  );
};
